import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'

import { formatMoney, shouldLock, type ContentType, type Entitlement } from '@lb/domain'
import { useTranslation } from '@lb/i18n'
import { LockedOverlay } from '@lb/ui'

/**
 * The paywall, in one place — because from 12 October it asks a different
 * question than it used to.
 *
 * ── `locked` is the answer; `shouldLock` is the fallback ──────────────────
 * Nine screens computed the lock themselves, from the viewer's entitlement and
 * the item's `premium` flag. That worked while entitlement was the only way in.
 * It stopped working the day a member could **buy one course**: a purchaser is
 * `entitled: false` and owns one row, so a client deriving the lock draws a
 * paywall over the thing they paid for — and cannot do otherwise, because
 * nothing it can see says which row is theirs.
 *
 * The server has been answering per row all along. `locked` is the field, and
 * the backend spec is explicit that it is the only one needed: *"a client tests
 * one field rather than inferring a paywall from `premium` and an entitlement
 * it cannot see."*
 *
 * `shouldLock` survives as the fallback for a response that carries no
 * `locked` at all — a server predating the field, which is the one production
 * runs today, and a fixture. It keeps its other honest job there too: a
 * `unknown` entitlement (the `/entitlement` read still in flight) shows the
 * content rather than flashing a lock over a paying subscriber's library.
 */
export function isLocked(
  item: { locked?: boolean | undefined; premium: boolean },
  entitlement: Entitlement | undefined,
): boolean {
  return item.locked ?? shouldLock(entitlement ?? 'unknown', item.premium)
}

/**
 * Whether a locked row can be bought on its own — `priceCents`, which the
 * domain's `isSoldSeparately` answers from the same field. Kept as a type here
 * so a call site can hand us a row of any content shape.
 */
export interface Sellable {
  id: string
  /** Only `COURSE` and `EVENT` are sellable, and the segment is read from it. */
  type: ContentType
  premium: boolean
  locked?: boolean | undefined
  priceCents?: number | null | undefined
}

/**
 * « Accès à vie », « Accès pendant 60 jours », or no claim at all.
 *
 * Since 12 October a course carries `accessDays` — the **offer's** window, in
 * days, `null` for lifetime. Three screens state it before payment and all
 * three used to have « à vie » written into the sentence, which was true of
 * every course ever sold and is about to be false of ten of them.
 *
 * `base` is the lifetime key; the window key is `base + 'Window'`, with
 * `count` for the plural. Returning **null** is the third state and it is not
 * a value: `SiteCourse` carries no `accessDays`, so a stranger's panel is
 * handed `undefined` and says nothing about duration. *For ever* is not a
 * safe guess on the last screen before a final, non-refundable sale, and a
 * missing term beats a wrong one until request 22 lands.
 *
 * A **date** never comes from here. What a buyer holds is `accessEndsAt` on
 * their own purchase, frozen at activation; this is only what is on offer.
 */
export function courseAccessTerm(
  base: string,
  days: number | null | undefined,
): { key: string; count?: number } | null {
  if (days === undefined) return null
  if (days === null) return { key: base }
  return { key: `${base}Window`, count: days }
}

/** The buy route's segment — the same word the library's own routes use. */
export function buySegment(type: ContentType): 'courses' | 'events' {
  return type === 'EVENT' ? 'events' : 'courses'
}

/**
 * A locked row, with the way out drawn on it.
 *
 * Two ways out, and the design puts both under every locked course: *« Included
 * with membership · or CA$49 »*. A row with **no** price is not an offer —
 * printables and podcasts are *« Members only. Not sold separately. »* — so it
 * keeps the plain lock and the caller's own reason line. One field, two states,
 * and nothing here branches on the content type.
 *
 * `reason` stays the caller's because the sentence is about the thing locked
 * (« Cet épisode… », « Cette réponse… ») and only the screen knows which it is.
 * It is replaced wholesale when there is a price, because the offer *is* the
 * reason then.
 */
export function PremiumLock({
  item,
  reason,
  children,
  className,
  reachable,
}: {
  item: Sellable
  reason: string
  children: ReactNode
  className?: string
  /**
   * The card beneath opens its own page (ISS-12). Passed by the grids, whose
   * cards have a page to open; left off by anything rendering a lock over
   * something that is not a link.
   */
  reachable?: boolean
}) {
  const { t, i18n } = useTranslation()
  const price = item.priceCents

  if (price == null) {
    return (
      <LockedOverlay
        kind="premium"
        reason={reason}
        className={className}
        reachable={reachable}
        action={
          <Link to="/plans" className="text-[13px] font-semibold text-ink underline">
            {t('resources.seePlans')}
          </Link>
        }
      >
        {children}
      </LockedOverlay>
    )
  }

  /*
   * CAD, because a content row carries a price and no currency — the platform
   * charges in one currency everywhere and the quote confirms it. The day a
   * second one exists this is the line that has to change, so it says so.
   */
  const money = formatMoney({ cents: price, currency: 'CAD' }, i18n.language)

  return (
    <LockedOverlay
      kind="premium"
      reason={t('lock.includedOrBuy', { price: money })}
      className={className}
      reachable={reachable}
      action={
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Link
            to="/buy/$segment/$id"
            params={{ segment: buySegment(item.type), id: item.id }}
            className="text-[13px] font-semibold text-ink underline"
          >
            {t('lock.buyAction', { price: money })}
          </Link>
          <Link to="/plans" className="text-[13px] text-muted underline hover:text-ink">
            {t('resources.seePlans')}
          </Link>
        </div>
      }
    >
      {children}
    </LockedOverlay>
  )
}
